import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import AppLayout from './layouts/AppLayout.jsx';
import BareLayout from './layouts/BareLayout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import {
  Unauthorized401,
  Forbidden403,
  NotFound404,
  ServerError500,
} from './pages/status/index.jsx';
import './App.scss';
import './styles/prospectus.scss';

const Home = lazy(() => import('./pages/home/index.jsx'));
const CourseCatalogue = lazy(() => import('./pages/course-catalogue/index.jsx'));
const ExpertOpenings = lazy(() => import('./pages/expert-openings/index.jsx'));
const RegistrationProfile = lazy(() => import('./pages/registration-profile/index.jsx'));
const LoginPage = lazy(() => import('./pages/login/index.jsx'));
const ForgotPassword = lazy(() => import('./pages/forgot-password/index.jsx'));
const ResetPassword = lazy(() => import('./pages/reset-password/index.jsx'));
const OAuthComplete = lazy(() => import('./pages/oauth-complete/index.jsx'));
const PlatformSettings = lazy(() => import('./pages/platform-settings/index.jsx'));
const LearnerDashboard = lazy(() => import('./pages/learner-dashboard/index.jsx'));
const IntakeAssessment = lazy(() => import('./pages/intake-assessment-form/index.jsx'));
const CohortEnrollmentContract = lazy(() =>
  import('./pages/cohort-enrollment-contract/index.jsx')
);
const CheckoutPayment = lazy(() => import('./pages/checkout-enrollment-payment/index.jsx'));
const CoursePlayer = lazy(() => import('./pages/course-player/index.jsx'));
const AssessmentCenter = lazy(() => import('./pages/assessment-center/index.jsx'));
const AcademyOperationsConsole = lazy(() =>
  import('./pages/academy-operations-console/index.jsx')
);
const CertificateView = lazy(() => import('./pages/certificate-web-view-badge/index.jsx'));
const CredentialVerificationPortal = lazy(() =>
  import('./pages/credential-verification-portal/index.jsx')
);
const SandboxCapstonePlayer = lazy(() => import('./pages/sandbox-capstone-player/index.jsx'));
const GraduateJobBoard = lazy(() => import('./pages/graduate-job-board/index.jsx'));
const CourseWorkspace = lazy(() => import('./pages/course-workspace/index.jsx'));
const CourseIntentCapture = lazy(() => import('./pages/course-intent-capture/index.jsx'));
const ModuleComposer = lazy(() => import('./pages/module-composer/index.jsx'));
const ExternalModulesManager = lazy(() => import('./pages/external-modules-manager/index.jsx'));
const RolesPermissionsApprovals = lazy(() => import('./pages/roles-permissions-approvals/index.jsx'));
const CertificationCriteriaManager = lazy(() => import('./pages/certification-criteria-manager/index.jsx'));
const GraduateRecordAdministrator = lazy(() => import('./pages/graduate-record-administrator/index.jsx'));
const CouponPaymentAdmin = lazy(() => import('./pages/coupon-payment-admin/index.jsx'));
const JobBoardModeration = lazy(() => import('./pages/job-board-moderation/index.jsx'));
const EmployerWorkspace = lazy(() => import('./pages/employer-operations-workspace/index.jsx'));
const RoleLandingHub = lazy(() => import('./pages/role-landing-hub/index.jsx'));
const CohortCommunications = lazy(() => import('./pages/cohort-communications/index.jsx'));
const AgentConsole = lazy(() => import('./pages/academic-design-agent-console/index.jsx'));
const FeedbackInsights = lazy(() => import('./pages/learner-feedback-insights-workspace/index.jsx'));
const LmaDashboard = lazy(() => import('./pages/learner-management-agent-dashboard/index.jsx'));

// Pages are generated per the design reference by ui-components-from-reference
// and registered as slug-based routes inside the AppLayout route group, e.g.:
//
//   const CourseCatalogue = lazy(() => import('./pages/course-catalogue/index.jsx'));
//   <Route path="course-catalogue" element={<CourseCatalogue />} />
//
// The layout's nav routes by slug (see config/menuData.js); until a page exists
// its slug falls through to the 404 catch-all below.

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public landing & gateway pages render chromeless (no global nav
                bar) via the bare layout. Registered before the guards. */}
            <Route element={<BareLayout />}>
              {/* Course Catalogue is the public default/home page */}
              <Route index element={<Home />} />
              <Route path="course-catalogue" element={<CourseCatalogue />} />
              {/* Public openings — the deployment/matchmaking teaser */}
              <Route path="expert-openings" element={<ExpertOpenings />} />

              {/* Public auth entry — must be registered before the route guards */}
              <Route path="login" element={<LoginPage />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="oauth-complete" element={<OAuthComplete />} />
            </Route>

            <Route element={<AppLayout />}>
              {/* Public adaptive intake assessment → career matching */}
              <Route path="intake-assessment-form" element={<IntakeAssessment />} />

              {/* Learner onboarding — public registration + profile config */}
              <Route path="registration-profile" element={<RegistrationProfile />} />

              {/* Public credential verification portal — open trust surface */}
              <Route
                path="credential-verification-portal"
                element={<CredentialVerificationPortal />}
              />

              {/* Authenticated Learner pages */}
              <Route element={<ProtectedRoute />}>
                <Route path="learner-dashboard" element={<LearnerDashboard />} />
                <Route
                  path="cohort-enrollment-contract"
                  element={<CohortEnrollmentContract />}
                />
                <Route
                  path="checkout-enrollment-payment"
                  element={<CheckoutPayment />}
                />
                <Route path="course-player" element={<CoursePlayer />} />
                <Route path="assessment-center" element={<AssessmentCenter />} />
                <Route path="certificate-web-view-badge" element={<CertificateView />} />
                <Route path="sandbox-capstone-player" element={<SandboxCapstonePlayer />} />
                <Route path="graduate-job-board" element={<GraduateJobBoard />} />
                <Route path="course-workspace" element={<CourseWorkspace />} />
                <Route path="course-intent-capture" element={<CourseIntentCapture />} />
                <Route path="module-composer" element={<ModuleComposer />} />
                <Route path="external-modules-manager" element={<ExternalModulesManager />} />
              </Route>

              {/* Academy Operations Console — Admin, plus Leadership for the
                  read-only Executive outcome-analytics dashboard. */}
              <Route element={<RoleRoute roles={['admin', 'leadership']} />}>
                <Route
                  path="academy-operations-console"
                  element={<AcademyOperationsConsole />}
                />
              </Route>

              {/* Remaining admin-only operations pages */}
              <Route element={<RoleRoute roles={['admin']} />}>
                <Route
                  path="roles-permissions-approvals-dashboard"
                  element={<RolesPermissionsApprovals />}
                />
                <Route
                  path="certification-criteria-manager"
                  element={<CertificationCriteriaManager />}
                />
                <Route
                  path="graduate-record-administrator"
                  element={<GraduateRecordAdministrator />}
                />
                <Route
                  path="coupon-payment-admin-console"
                  element={<CouponPaymentAdmin />}
                />
                <Route path="platform-settings" element={<PlatformSettings />} />
                <Route
                  path="job-board-moderation-console"
                  element={<JobBoardModeration />}
                />
              </Route>

              {/* Employer recruitment workspace — employer (or admin) only */}
              <Route element={<RoleRoute roles={['employer', 'admin']} />}>
                <Route
                  path="employer-operations-workspace"
                  element={<EmployerWorkspace />}
                />
              </Route>

              {/* Mentor / Course Supervisor Role Landing Hub + communications */}
              <Route element={<RoleRoute roles={['mentor', 'course supervisor', 'leadership', 'admin']} />}>
                <Route path="role-landing-hub" element={<RoleLandingHub />} />
                <Route path="cohort-communications-announcements" element={<CohortCommunications />} />
              </Route>

              {/* Academic Design Agent Console — agent / architect / admin */}
              <Route element={<RoleRoute roles={['academic design agent', 'course architect', 'admin']} />}>
                <Route path="academic-design-agent-console" element={<AgentConsole />} />
                <Route path="learner-feedback-insights-workspace" element={<FeedbackInsights />} />
              </Route>

              {/* Learner Management Agent Dashboard — agent / mentor / supervisor / leadership / admin */}
              <Route element={<RoleRoute roles={['learner course management agent', 'mentor', 'course supervisor', 'leadership', 'admin']} />}>
                <Route path="learner-management-agent-dashboard" element={<LmaDashboard />} />
              </Route>

              {/* Generated slug-based page routes go here */}

              {/* Generic status pages */}
              <Route path="401" element={<Unauthorized401 />} />
              <Route path="403" element={<Forbidden403 />} />
              <Route path="404" element={<NotFound404 />} />
              <Route path="500" element={<ServerError500 />} />

              {/* Catch-all → 404 */}
              <Route path="*" element={<NotFound404 />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
